import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'

// API Response types
export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  error?: string
  code?: string
  details?: any
}

// Auth types
export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  success: boolean
  user: {
    userId: string
    email: string
  }
}

export interface SignupRequest {
  email: string
  password: string
  confirmPassword: string
  firstName?: string
  lastName?: string
  company?: string
}

export interface SignupResponse {
  userId: string
  email: string
  isActive: boolean
  isVerified: boolean
  verificationToken?: string
  message: string
}

// User types
export interface UserProfile {
  userId: string
  email: string
  firstName?: string
  lastName?: string
  company?: string
  isActive: boolean
  isVerified: boolean
  createdAt: string
  updatedAt: string
  lastLoginAt?: string
  securityStatus: {
    hasActiveApiKeys: boolean
    accountLocked: boolean
    failedLoginAttempts: number
    emailVerified: boolean
    recentActivity: boolean
  }
  statistics: {
    totalApiKeys: number
    activeApiKeys: number
    recentApiKeys: number
    lastApiKeyUsage?: string
    usage: {
      totalRequests: number
      uniqueEndpoints: number
      uniqueModels: number
      totalTokens: number
      totalCost: number
      lastRequest?: string
      avgRequestDuration: number
    }
  }
}

// API Key types
export interface ApiKey {
  userApiKey: string
  keyName?: string
  description?: string
  createdAt: string
  lastUsed?: string
  usageStats: {
    total_requests: number
    models_used: string[]
    daily_usage: Record<string, any>
    hourly_usage: Record<string, any>
    endpoints: Record<string, number>
  }
  isActive: boolean
  expiresAt?: string
}

export interface CreateApiKeyRequest {
  keyName?: string
  description?: string
  rateLimits?: {
    perMinute?: number
    perHour?: number
    perDay?: number
  }
  expiresAt?: string
  scopes?: string[]
}

export interface CreateApiKeyResponse {
  userApiKey: string
  keyName?: string
  description?: string
  scopes: string[]
  rateLimits: {
    perMinute: number
    perHour: number
    perDay: number
  }
  expiresAt?: string
  message: string
}

// Analytics types
export interface UsageAnalytics {
  keyName?: string
  createdAt: string
  lastUsed?: string
  usageStats: {
    total_requests: number
    models_used: string[]
    daily_usage: Record<string, any>
    hourly_usage: Record<string, any>
    endpoints: Record<string, number>
  }
  analytics: {
    totalRequests: number
    uniqueEndpoints: number
    uniqueModels: number
    firstUsed?: string
    lastUsed?: string
    totalTokens: number
    totalCost: number
    avgRequestDuration: number
  }
  dailyUsage: Array<{
    date: string
    requests: number
    tokens: number
    cost: number
  }>
  endpointUsage: Array<{
    endpoint: string
    requests: number
    avgDuration: number
    tokens: number
  }>
  modelUsage: Array<{
    model_used: string
    requests: number
    tokens: number
    cost: number
  }>
}

// Error handling
export interface ApiError extends Error {
  code?: string
  details?: any
  status?: number
}

class ApiClient {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getAuthToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle errors
    this.instance.interceptors.response.use(
      (response) => {
        return response
      },
      (error: AxiosError) => {
        const apiError: ApiError = new Error(error.message)

        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          apiError.status = error.response.status
          apiError.message = (error.response.data as any)?.message || error.message
          apiError.code = (error.response.data as any)?.code
          apiError.details = (error.response.data as any)?.details

          // Handle authentication errors
          if (error.response.status === 401) {
            this.clearAuth()
            window.location.href = '/login'
          }
        } else if (error.request) {
          // The request was made but no response was received
          apiError.message = 'No response from server'
          apiError.code = 'NETWORK_ERROR'
        } else {
          // Something happened in setting up the request that triggered an Error
          apiError.message = 'Request setup failed'
          apiError.code = 'REQUEST_ERROR'
        }

        return Promise.reject(apiError)
      }
    )
  }

  // Auth token management
  private getAuthToken(): string | null {
    return localStorage.getItem('accessToken')
  }

  private setAuthToken(token: string): void {
    localStorage.setItem('accessToken', token)
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem('refreshToken', token)
  }

  clearAuth(): void {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }

  // Auth methods
  async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      // For user auth, we need to use the user API key format
      // The backend expects a Bearer token with user API key
      const response = await this.instance.post<ApiResponse<LoginResponse>>(
        '/v1/user/auth',
        data
      )

      if (response.data.success && response.data.data) {
        // Store the user API key as the access token
        this.setAuthToken(data.email) // This is a placeholder - in real implementation, backend should return tokens
        return response.data.data
      }

      throw new Error(response.data.message || 'Login failed')
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  async signup(data: SignupRequest): Promise<SignupResponse> {
    try {
      const response = await this.instance.post<ApiResponse<SignupResponse>>(
        '/v1/user/register',
        data
      )

      if (response.data.success && response.data.data) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Signup failed')
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  async logout(): Promise<void> {
    try {
      await this.instance.post('/v1/user/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      this.clearAuth()
    }
  }

  // User methods
  async getUserProfile(): Promise<UserProfile> {
    try {
      const response = await this.instance.get<ApiResponse<UserProfile>>(
        '/v1/user/profile'
      )

      if (response.data.success && response.data.data) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Failed to get user profile')
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  // API Key methods
  async getApiKeys(): Promise<ApiKey[]> {
    try {
      const response = await this.instance.get<ApiResponse<ApiKey[]>>(
        '/v1/user/keys'
      )

      if (response.data.success && response.data.data) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Failed to get API keys')
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  async createApiKey(data: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    try {
      const response = await this.instance.post<ApiResponse<CreateApiKeyResponse>>(
        '/v1/user/keys/create',
        data
      )

      if (response.data.success && response.data.data) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Failed to create API key')
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  async deleteApiKey(apiKey: string): Promise<void> {
    try {
      await this.instance.delete('/v1/user/keys', {
        params: { key: apiKey },
      })
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  // Analytics methods
  async getUserStats(): Promise<any> {
    try {
      const response = await this.instance.get<ApiResponse<any>>(
        '/v1/user/stats'
      )

      if (response.data.success && response.data.data) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Failed to get user stats')
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  async getApiKeyUsageStats(apiKey: string): Promise<UsageAnalytics> {
    try {
      const response = await this.instance.get<ApiResponse<UsageAnalytics>>(
        `/v1/user/keys/${apiKey}/stats`
      )

      if (response.data.success && response.data.data) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Failed to get API key usage stats')
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await this.instance.get('/health')
      return response.data
    } catch (error) {
      throw this.handleError(error as ApiError)
    }
  }

  // Error handler
  private handleError(error: ApiError): ApiError {
    console.error('API Error:', error)
    return error
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.getAuthToken()
  }

  getUserFromStorage(): any {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  }

  setUserInStorage(user: any): void {
    localStorage.setItem('user', JSON.stringify(user))
  }
}

// Export singleton instance
export const api = new ApiClient()